import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_IN_SHOP_COLUMNS,
  IN_SHOP_ORDERS,
  normalizeInShopColumns,
  type InShopColumn,
  type InShopDeadlineStatus,
  type InShopOrder,
} from '@mustauto/types/in-shop';
import { api } from '@/lib/api';

export type InShopLocation = 'bay' | 'paint_booth' | 'no_slot' | 'outsourced' | 'ready';

export interface InShopEntry {
  osId: string;
  number: number;
  plate: string;
  vehicleName: string;
  color: string | null;
  customerName: string | null;
  customerPhone: string | null;
  mechanicName: string | null;
  status: string;
  location: InShopLocation;
  /** O nome da vaga, ou o rótulo do grupo. */
  locationLabel: string;
  slotLabel: string | null;
  stageLabel: string | null;
  providers: string[];
  enteredAt: string;
  daysInShop: number;
  completedAt: string | null;
  /** Quando o serviço terminou (aguardando pagamento ou concluída), ISO. */
  serviceDoneAt: string | null;
  /** Previsão de entrega, ISO. */
  deadline: string | null;
  deadlineStatus: InShopDeadlineStatus;
  /** Dias de atraso no dia da oficina (no `late`), ou dias depois do prazo em que o serviço terminou (no `done`). */
  deadlineDaysLate: number;
}

export interface CarsInShopData {
  generatedAt: string;
  readyWindowDays: number;
  /** Concluídas há mais de `readyWindowDays` sem entrega marcada — fora da lista. */
  olderReadyCount: number;
  lateCount: number;
  dueTodayCount: number;
  vehicles: InShopEntry[];
}

/**
 * Carros na oficina agora, e onde estão. A chave começa com `service-orders`
 * para o `useOSRealtime` atualizar a tela junto com a lista de OS quando uma
 * muda de status ou de etapa.
 */
export function useCarsInShop(workshopId: string | null) {
  return useQuery<CarsInShopData>({
    queryKey: ['service-orders', 'in-shop'],
    queryFn: async () => (await api.get('/service-orders/in-shop')).data,
    enabled: !!workshopId,
    refetchInterval: 60_000,
  });
}

const COLUMNS_KEY = 'mustauto:in-shop:columns';
const ORDER_KEY = 'mustauto:in-shop:order';

function readColumns(): InShopColumn[] {
  try {
    const raw = localStorage.getItem(COLUMNS_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return normalizeInShopColumns(parsed);
    }
  } catch {
    // Navegador sem armazenamento (janela anônima, bloqueio): vale o padrão.
  }
  return [...DEFAULT_IN_SHOP_COLUMNS];
}

function readOrder(): InShopOrder {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (raw && (IN_SHOP_ORDERS as readonly string[]).includes(raw)) return raw as InShopOrder;
  } catch {
    // Idem: sem armazenamento, a ordem por lugar.
  }
  return 'location';
}

/**
 * As colunas e a ordem da tela, guardadas neste navegador. A exportação usa
 * as mesmas: uma escolha só, para o PDF não sair diferente da tela.
 */
export function useInShopView() {
  const [columns, setColumnsState] = useState<InShopColumn[]>(readColumns);
  const [order, setOrderState] = useState<InShopOrder>(readOrder);

  const setColumns = useCallback((next: readonly InShopColumn[]) => {
    const normalized = normalizeInShopColumns(next);
    setColumnsState(normalized);
    try {
      localStorage.setItem(COLUMNS_KEY, JSON.stringify(normalized));
    } catch {
      // Sem armazenamento a escolha vale até fechar a aba.
    }
  }, []);

  const setOrder = useCallback((next: InShopOrder) => {
    setOrderState(next);
    try {
      localStorage.setItem(ORDER_KEY, next);
    } catch {
      // Idem.
    }
  }, []);

  return { columns, setColumns, order, setOrder };
}
